import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RightsManagementState } from '../../models/user-management.models';

export const selectUserManagementState = createFeatureSelector<RightsManagementState>('userManagement');

// Basic selectors
export const selectUsers = createSelector(selectUserManagementState, state => state.users);

export const selectRoles = createSelector(selectUserManagementState, state => state.roles);

export const selectRights = createSelector(selectUserManagementState, state => state.assignedRights ?? []);

// Selector to get full Right objects (for display purposes)
export const selectRightsObjects = createSelector(selectUserManagementState, state => state.assignedRights ?? []);

export const selectSelectedUser = createSelector(selectUserManagementState, state => state.selectedUser);

export const selectLoading = createSelector(selectUserManagementState, state => state.loading);

export const selectError = createSelector(selectUserManagementState, state => state.error);

export const selectSearchTerm = createSelector(selectUserManagementState, state => state.searchTerm);

export const selectFilters = createSelector(selectUserManagementState, state => state.filters);

// Computed selectors
export const selectFilteredUsers = createSelector(selectUsers, selectSearchTerm, selectFilters, (users, searchTerm) => {
    let filteredUsers = users;

    // Apply search filter
    if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredUsers = filteredUsers.filter(
            user =>
                user.displayName?.toLowerCase().includes(term) ||
                user.email.toLowerCase().includes(term) ||
                user.firstName?.toLowerCase().includes(term) ||
                user.familyName?.toLowerCase().includes(term)
        );
    }

    return filteredUsers;
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const selectUsersByRole = createSelector(selectFilteredUsers, selectRoles, (users, _roles) => {
    // For now, returning all users
    return users;
});

export const selectRoleById = (roleId: string) => createSelector(selectRoles, roles => roles.find(role => role.id === roleId));

export const selectRightById = (rightId: string) => createSelector(selectRightsObjects, rights => rights.find(right => right === rightId));

export const selectUserRoles = createSelector(selectSelectedUser, selectRoles, (selectedUser, roles) => {
    if (!selectedUser?.roles?.length || !roles) return [];
    return roles.filter(role => selectedUser.roles.includes(role.id));
});

export const selectUserRights = createSelector(selectSelectedUser, selectRightsObjects, (selectedUser, rights) => {
    if (!selectedUser?.rights?.length) return [];
    return rights.filter(right => selectedUser.rights.includes(right));
});

export const selectAvailableRoles = createSelector(selectRoles, selectSelectedUser, roles => roles ?? []);

export const selectAvailableRights = createSelector(selectRightsObjects, selectSelectedUser, rights => rights ?? []);

// Permission-based selectors
export const selectCanEditUsers = createSelector(
    selectUserManagementState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _state => true // This should be based on current user permissions
);

export const selectCanEditRoles = createSelector(
    selectUserManagementState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _state => true // This should be based on current user permissions
);

export const selectSelectedRole = createSelector(selectUserManagementState, state => state.selectedRole);
